<template>
    <div v-if="theItemDetails" class="container-fluid">
        
        <!-- control the device display globally -->
        <div class="row">
            <div class="offset-lg-2 col-lg-8 col-12">
                <!-- item main photo -->
                <div class="row mt-2">
                    <div class="col-12">
                        <div 
                            @click.prevent="itemPhotosPreviewFxn(0)" 
                            class="listing-img-box rounded clickable" 
                            v-bind:style="{ backgroundImage: 'url(' + mainItemPhoto(theItemDetails.photos) + ')' }"
                        ></div>
                    </div>
                </div>

                <!-- item extra photos -->
                <div v-if="showExtraPhotos" class="mt-2">
                    <app-item-extra-photos 
                        :photos="theItemDetails.photos" 
                        :itemId="theItemDetails._id" 
                        :itemTitle="theItemDetails.title" 
                        @emitPhotosPreviewFxn="itemPhotosPreviewFxn" 
                    ></app-item-extra-photos>
                </div>

                <!-- item title & buttons -->
                <div class="row mt-3">
                    <div class="col-12">
                        <p class="listing-item-title mb-2">{{theItemDetails.title}}</p>
                        <p class="listing-item-price my-1">
                            <app-item-price :item="theItemDetails"></app-item-price>
                        </p>

                        <!-- import items action btns -->
                        <app-item-action-btns :item="theItemDetails" :pageName="'ItemDetails'"></app-item-action-btns>
                    </div>
                </div>

                <!-- item full details -->
                <div class="row mt-3">
                    <div class="col-12">
                        <p class="listing-item-details">
                            <i class="fas fa-layer-group text-muted fa-sm"></i> 
                            {{theItemDetails.category | capitalize}} 
                            <span v-if="theItemDetails.subCategory">({{theItemDetails.subCategory | capitalize}})</span>
                        </p>
                        <p class="listing-item-details">
                            <i class="fas fa-balance-scale text-muted fa-sm"></i> 
                            {{theItemDetails.condition | capitalize}}
                        </p>
                        <p class="listing-item-details">
                            <i class="fas fa-history text-muted fa-sm"></i> 
                            Posted {{theItemDetails.joined | postedTimeAgo}}
                        </p>
                        <p><i class="fas fa-file-alt text-muted fa-sm"></i> Item Description</p>
                        <p class="listing-item-description">{{theItemDetails.description}}</p>
                    </div>
                </div>

                <!-- share item button -->
                <div class="row">
                    <div class="col-12">
                        <hr class="m-0">
                        <ul class="nav justify-content-center text-muted">
                            <li class="nav-item link-anewitem clickable">
                                <a v-if="copied" class="nav-link">
                                    <i class="fas fa-copy text-dark-green"></i> Copied!
                                </a>
                                <a v-else @click="shareItemFxn" class="nav-link" title="share">
                                    <i class="fas fa-share-alt text-dark-green"></i> Share Item
                                </a>
                            </li>
                            <li class="nav-item link-anewitem">
                                <a class="nav-link" title="save">
                                    <i class="fas fa-save text-dark-green"></i>
                                    <span>{{itemSaves}}</span>
                                </a>
                            </li>
                        </ul>
                        <hr class="m-0">
                    </div>
                </div>
                
                <!-- related items -->
                <div class="my-5">
                    <app-related-items :relatedCategory="theItemDetails.category" :excludeItemId="theItemDetails._id"></app-related-items>
                </div>
                
                <!-- explore categories -->
                <div class="my-5">
                    <app-more-categories :displayedCategory="theItemDetails.category"></app-more-categories>
                </div>

                <!-- just for space -->
                <div class="my-5"></div>
                
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { appendImgHostUrl, shareItem, copyTextFxn, webpageTitle } from '@/store/dry'

import LayoutGeneral from '@/layouts/General.vue'
import ItemExtraPhotos from './itemDetailsParts/ItemExtraPhotos.vue'
import RelatedItems from './itemDetailsParts/RelatedItems.vue'
import MoreCategories from './itemDetailsParts/MoreCategories.vue'
import ItemActionBtns from './itemDetailsParts/ItemActionBtns.vue'
import ItemPrice from '../shared/ItemParts/ItemPrice.vue'

export default {
    name: 'ItemDetails',
    props: {
        itemTitle: {
            type: String,
            required: false
        },
        itemId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            copied: false
        }
    },
    methods: {
        ...mapActions(['itemDetailsAction']),
        itemDetailsFxn(theItemId) {
            let itemObj = {
                itemId: theItemId
            }
            this.itemDetailsAction(itemObj).then(() => {}).catch((e) => {})
        },
        mainItemPhoto(itemPhotos) {
            if(itemPhotos){
                let src = ""
                for(let x=0; x < itemPhotos.length; x++){
                    if(itemPhotos[x].main){
                        // fetch the path to the hd quality version of the item photos via photoObj.path
                        src = appendImgHostUrl(itemPhotos[x].path)
                    }
                }
                if(src !== ""){
                    return src
                }
                return this.photoHolder
            }
            return this.photoHolder
        },
        shareItemFxn() {
            let shareObj = {
                title: document.title,
                text: `Check out this item for sale - ${this.theItemDetails.title} on aNewItem.com`,
                url: `${process.env.VUE_APP_CLT_URL}/site/item/${this.theItemDetails.title}/${this.theItemDetails._id}`
            }
            let shareResponse = shareItem(shareObj)
            if(!shareResponse){
                this.copied = true
                setTimeout(() => {
                    this.copied = false
                }, 1000)
                let shareNote = `${shareObj.text} \n ${shareObj.url}`
                copyTextFxn(shareNote)
            }
        },
        itemPhotosPreviewFxn(imageIndex) {
            /**
             * if the user clicked a specific image, convert the imageIndex value to string, 
             * this allows it to be sent over props and accepted by the ItemPhotosAlbum page
             * then redirect to the photos album preview page
             * */ 
			this.$router.push({
				name: 'photos-album',
				params: {
					itemTitle: this.itemTitle,
					itemId: this.itemId,
                    photoIndex: `${imageIndex}` 
				}
			}).catch((e) => {})
		},
    },
    computed: {
        ...mapGetters(['itemDetails']),
        theItemDetails() {
            if(this.itemDetails){
                webpageTitle(`${this.itemDetails.title}`)
                return this.itemDetails
            }
            return false
        },
        showExtraPhotos() {
            if(this.theItemDetails && this.theItemDetails.photos && this.theItemDetails.photos.length > 1) {
                return true
            }
            return false
        },
        itemSaves() {
            if(this.theItemDetails.saves > 1){
                return ` ${this.theItemDetails.saves} Saves`
            }
            return ` ${this.theItemDetails.saves} Save`
        },
    },
    components: {
        appItemExtraPhotos: ItemExtraPhotos,
        appRelatedItems: RelatedItems,
        appMoreCategories: MoreCategories,
        appItemActionBtns: ItemActionBtns,
        appItemPrice: ItemPrice
    },
    watch: {
        itemId() {
            this.itemDetailsFxn(this.itemId)
        }
    },
    created() {
        this.itemDetailsFxn(this.itemId)
        this.$emit('update:layout', LayoutGeneral);
    }
}
</script>